// src/web/client/src/pages/Automations.jsx
//
// Automations — every email the app sends on its own, ONE card per email type
// with ONE switch each (UX brief, Phase B3): the thank-you and the review
// request this app owns; the restock reminder and the loyalty thank-you the
// brain decides (Platform plan — locked card otherwise, the same pattern as
// every other brain screen). Then the templates, the timing (Pro+) and A/B.
//
// All four switches read and write the store's one settings object
// (hooks/useShopSettings): thank_you_enabled / review_request_enabled are this
// app's; platform_emails.{reorder,review,loyalty} are mirrored to the Platform,
// which then schedules and sends nothing of that kind. On the Platform plan the
// review switch flips both its own review requests and the brain's, so "off"
// means no review request from anywhere.

import React from 'react';
import PageTabs from '@/components/PageTabs';
import PageHeader from '@/components/PageHeader';
import { PlatformGate, PLATFORM_PAGES } from '@/pages/Platform';
import { useSubscriptionStatus } from '@/components/subscription/SubscriptionGuard';
import { useShopSettings } from '@/hooks/useShopSettings';
import { useToast } from '@/components/ui/use-toast';
import { ThankYouCard, ReviewRequestCard, useTypeStats, useTemplateStates, useTurnOn } from '@/components/automations/RetainEmailCards';
import Messages from '@/pages/Messages';
import SmartTiming from '@/components/analytics/SmartTiming';
import ABTesting from '@/components/analytics/ABTesting';
import { safeApi } from '@/utils/api';

function EmailCards() {
  const subscription = useSubscriptionStatus();
  const hasBrain = subscription?.hasFeatureAccess?.('platform_access') === true;
  const { settings, update, applyLocal, saving, loading, domain } = useShopSettings();
  const perf = useTypeStats();
  const { states: templateStates, setState: setTemplateState } = useTemplateStates(domain);
  const { turnOn, turningOn } = useTurnOn({ applyLocal, setState: setTemplateState });
  const { toast } = useToast();
  const [brainReviews, setBrainReviews] = React.useState(null);

  React.useEffect(() => {
    if (!hasBrain) return undefined;
    let alive = true;
    (async () => {
      const r = await safeApi.get('/platform/reviews/summary');
      if (alive) setBrainReviews(r && typeof r.requestsSent === 'number' ? r.requestsSent : null);
    })();
    return () => { alive = false; };
  }, [hasBrain]);

  const brainSwitch = (kind) => async (v) => {
    const r = await update({ platform_emails: { [kind]: v } });
    if (!r.ok) toast({ title: 'Could not save the switch', description: r.error?.message || 'Please try again.', variant: 'destructive' });
  };
  const kindOn = (kind) => settings?.platform_emails?.[kind] !== false;
  const switchDisabled = saving || loading || !settings;

  return (
    <div className="space-y-6">
      <ThankYouCard
        settings={settings} update={update} saving={switchDisabled} perf={perf}
        templateState={templateStates?.thank_you ?? null}
        onTurnOn={() => turnOn('thank_you', 'thank_you_enabled', 'the thank-you email')}
        turningOn={turningOn === 'thank_you'}
      />
      <ReviewRequestCard
        settings={settings} update={update} saving={switchDisabled} perf={perf} hasBrain={hasBrain} brainRequested={brainReviews ?? undefined}
        templateState={templateStates?.review_request ?? null}
        onTurnOn={() => turnOn('review_request', 'review_request_enabled', 'review requests')}
        turningOn={turningOn === 'review_request'}
      >
        {hasBrain ? <PlatformGate><PLATFORM_PAGES.reviews /></PlatformGate> : null}
      </ReviewRequestCard>
      <PlatformGate>
        <PLATFORM_PAGES.restockCard enabled={kindOn('reorder')} onToggle={brainSwitch('reorder')} switchDisabled={switchDisabled} />
      </PlatformGate>
      <PlatformGate>
        <PLATFORM_PAGES.loyaltyCard enabled={kindOn('loyalty')} onToggle={brainSwitch('loyalty')} switchDisabled={switchDisabled} />
      </PlatformGate>
    </div>
  );
}

export default function Automations() {
  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader title="Automations" subtitle="The emails that go out on their own — one switch for each" />
        <PageTabs tabs={[
          { id: 'emails', label: 'Emails', render: () => <EmailCards /> },
          { id: 'templates', label: 'Templates', render: () => <Messages /> },
          { id: 'timing', label: 'Timing', render: () => <SmartTiming /> },
          { id: 'ab', label: 'A/B tests', render: () => <ABTesting /> },
        ]} />
      </div>
    </div>
  );
}
